import { useCallback, useRef, useEffect, useState } from 'react'

/**
 * Sound definitions with their paths and default settings
 */
const SOUNDS = {
  // Messages
  message: { path: '/sounds/message.wav', category: 'messages', label: 'New Message' },
  typing: { path: '/sounds/typing.wav', category: 'messages', label: 'Typing Indicator' },

  // Calls
  callRing: { path: '/sounds/call-ring.wav', category: 'calls', label: 'Incoming Call' },
  calling: { path: '/sounds/calling.wav', category: 'calls', label: 'Outgoing Call' },
  callConnect: { path: '/sounds/call-connect.wav', category: 'calls', label: 'Call Connected' },
  callEnd: { path: '/sounds/call-end.wav', category: 'calls', label: 'Call Ended' },
  hangup: { path: '/sounds/hangupcall.wav', category: 'calls', label: 'Hang Up' },
  mute: { path: '/sounds/mute.wav', category: 'calls', label: 'Mute Toggle' },
  deafen: { path: '/sounds/deafen.wav', category: 'calls', label: 'Deafen Toggle' },

  // Notifications
  notification: { path: '/sounds/notification.wav', category: 'notifications', label: 'General Notification' },
  friendRequest: { path: '/sounds/friend-request.wav', category: 'notifications', label: 'Friend Request' },

  // Feedback
  success: { path: '/sounds/success.wav', category: 'feedback', label: 'Success' },
  error: { path: '/sounds/error.wav', category: 'feedback', label: 'Error' },
}

// Sound categories for settings UI
export const SOUND_CATEGORIES = {
  messages: { label: 'Messages', icon: '💬' },
  calls: { label: 'Calls', icon: '📞' },
  notifications: { label: 'Notifications', icon: '🔔' },
  feedback: { label: 'Feedback', icon: '✨' },
}

// Get sounds by category
export const getSoundsByCategory = (category) => {
  return Object.entries(SOUNDS)
    .filter(([, config]) => config.category === category)
    .map(([key, config]) => ({ key, ...config }))
}

// Get all sounds
export const getAllSounds = () => {
  return Object.entries(SOUNDS).map(([key, config]) => ({ key, ...config }))
}

// Default sound settings stored in localStorage
const DEFAULT_SOUND_SETTINGS = {
  enabled: true,
  volume: 0.7,
  sounds: Object.keys(SOUNDS).reduce((acc, key) => {
    acc[key] = { enabled: true, volume: 1.0 }
    return acc
  }, {})
}

const STORAGE_KEY = 'gjyl_sound_settings'

/**
 * useSounds - Hook for managing app sounds
 *
 * Features:
 * - Play sounds by name
 * - Volume control (master + per-sound)
 * - Enable/disable sounds
 * - Persist settings to localStorage
 * - Preview sounds in settings
 */
export function useSounds() {
  const audioRefs = useRef({})
  const previewAudioRef = useRef(null)
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Merge with defaults in case new sounds were added
        return {
          ...DEFAULT_SOUND_SETTINGS,
          ...parsed,
          sounds: { ...DEFAULT_SOUND_SETTINGS.sounds, ...parsed.sounds }
        }
      }
    } catch (e) {
      console.error('Failed to load sound settings:', e)
    }
    return DEFAULT_SOUND_SETTINGS
  })

  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch (e) {
      console.error('Failed to save sound settings:', e)
    }
  }, [settings])

  // Preload audio files
  useEffect(() => {
    Object.entries(SOUNDS).forEach(([key, config]) => {
      if (!audioRefs.current[key]) {
        const audio = new Audio(config.path)
        audio.preload = 'auto'
        audioRefs.current[key] = audio
      }
    })

    return () => {
      // Cleanup
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause()
        audio.src = ''
      })
      audioRefs.current = {}
    }
  }, [])

  /**
   * Play a sound by key
   * @param {string} soundKey - The sound key (e.g., 'message', 'callRing')
   * @param {object} options - Optional overrides { volume }
   */
  const play = useCallback((soundKey, options = {}) => {
    // Check if sounds are enabled globally
    if (!settings.enabled) return

    // Check if this specific sound exists and is enabled
    const soundConfig = SOUNDS[soundKey]
    const soundSetting = settings.sounds[soundKey]

    if (!soundConfig || !soundSetting?.enabled) return

    const audio = audioRefs.current[soundKey]
    if (!audio) return

    // Calculate volume: master * sound-specific * override
    const volume = settings.volume * (soundSetting.volume || 1.0) * (options.volume || 1.0)

    // Clone the audio for overlapping sounds
    const playableAudio = audio.cloneNode()
    playableAudio.volume = Math.min(1, Math.max(0, volume))

    playableAudio.play().catch(err => {
      // Silently fail - usually means user hasn't interacted with page yet
      console.debug(`Could not play sound ${soundKey}:`, err.message)
    })
  }, [settings])

  /**
   * Preview a sound (ignores enabled settings)
   * Stops any currently playing preview before starting new one
   */
  const preview = useCallback((soundKey, volume = 0.5) => {
    const soundConfig = SOUNDS[soundKey]
    if (!soundConfig) return

    // Stop any currently playing preview
    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current.currentTime = 0
      previewAudioRef.current = null
    }

    const audio = new Audio(soundConfig.path)
    audio.volume = Math.min(1, Math.max(0, settings.volume * volume))
    audio.loop = false // Ensure no looping

    // Store reference so we can stop it if needed
    previewAudioRef.current = audio

    // Clear reference when audio ends
    audio.addEventListener('ended', () => {
      if (previewAudioRef.current === audio) {
        previewAudioRef.current = null
      }
    })

    audio.play().catch(err => {
      console.debug(`Could not preview sound ${soundKey}:`, err.message)
      previewAudioRef.current = null
    })
  }, [settings.volume])

  /**
   * Stop any currently playing preview
   */
  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current.currentTime = 0
      previewAudioRef.current = null
    }
  }, [])

  /**
   * Update master settings
   */
  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({ ...prev, ...updates }))
  }, [])

  /**
   * Update individual sound settings
   */
  const updateSoundSetting = useCallback((soundKey, updates) => {
    setSettings(prev => ({
      ...prev,
      sounds: {
        ...prev.sounds,
        [soundKey]: { ...prev.sounds[soundKey], ...updates }
      }
    }))
  }, [])

  /**
   * Toggle master sound on/off
   */
  const toggleMaster = useCallback(() => {
    setSettings(prev => ({ ...prev, enabled: !prev.enabled }))
  }, [])

  /**
   * Toggle individual sound on/off
   */
  const toggleSound = useCallback((soundKey) => {
    setSettings(prev => ({
      ...prev,
      sounds: {
        ...prev.sounds,
        [soundKey]: {
          ...prev.sounds[soundKey],
          enabled: !prev.sounds[soundKey]?.enabled
        }
      }
    }))
  }, [])

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_SOUND_SETTINGS)
  }, [])

  return {
    // State
    settings,
    enabled: settings.enabled,
    volume: settings.volume,

    // Actions
    play,
    preview,
    stopPreview,
    updateSettings,
    updateSoundSetting,
    toggleMaster,
    toggleSound,
    resetToDefaults,

    // Constants
    SOUNDS,
    SOUND_CATEGORIES,
  }
}

export default useSounds
